import { useMemo, useState } from 'react'
import {
  Search,
  Package,
  FolderOpen,
  Folder,
  Baby,
  Archive,
  HelpCircle,
  ChevronDown,
  ChevronRight,
  Sprout,
  Link2,
  ArrowUpRight,
  Plus,
  AlertTriangle,
  Info,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { AdTypeBadge, StateBadge } from '@/components/StatusBadge'
import {
  isPortfolioMockActive,
  useParents,
  useParentTree,
  useUnmatchedCampaigns,
  type PortfolioCampaign,
  type PortfolioParent,
  type PortfolioUnmatchedCampaign,
} from '@/api/portfolios'
import ReParentDialog from './ReParentDialog'
import CampaignAssignmentDialog from './CampaignAssignmentDialog'
import PortfolioBuilderWizard from './PortfolioBuilderWizard'

type FilterChip = 'all' | 'harvested' | 'legacy' | 'review' | 'performing'

const FILTER_CHIPS: Array<{ id: FilterChip; label: string }> = [
  { id: 'all', label: 'Alle' },
  { id: 'harvested', label: 'Nur ausgeschlachtet' },
  { id: 'legacy', label: 'Nur Legacy' },
  { id: 'review', label: 'Nur Review' },
  { id: 'performing', label: 'Nur performt' },
]

function fmtEur(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`
}
function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return `${n.toFixed(1)}%`
}
function fmtOrders(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return String(n)
}

function CampaignPerformanceBadge({ campaign }: { campaign: PortfolioCampaign }) {
  // Heuristik aus der Spec: harvested wenn keine neuen Terms + wenig Spend,
  // Review wenn ACoS hoch, performing wenn Orders > 0 und ACoS moderat
  const status = deriveStatus(campaign)
  const map: Record<'performing' | 'review' | 'harvested' | 'inactive', { label: string; cls: string }> = {
    performing: { label: 'performt', cls: 'bg-emerald-100 text-emerald-800' },
    review: { label: 'Review', cls: 'bg-amber-100 text-amber-800' },
    harvested: { label: 'ausgeschlachtet', cls: 'bg-sky-100 text-sky-800' },
    inactive: { label: 'inaktiv', cls: 'bg-zinc-100 text-zinc-600' },
  }
  const m = map[status]
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium', m.cls)}>
      {m.label}
    </span>
  )
}

function deriveStatus(c: PortfolioCampaign): 'performing' | 'review' | 'harvested' | 'inactive' {
  if (c.state !== 'enabled') return 'inactive'
  const acos = c.acos30d ?? 0
  const spend = c.spend30d ?? 0
  const orders = c.orders30d ?? 0
  if (orders > 0 && acos > 0 && acos <= 30) return 'performing'
  if (acos > 50) return 'review'
  if (spend < 5 && orders === 0) return 'harvested'
  return 'review'
}

function matchesTextFilter(fields: Array<string | null | undefined>, q: string): boolean {
  if (!q) return true
  const qLower = q.toLowerCase()
  return fields.some((f) => (f ?? '').toLowerCase().includes(qLower))
}

export default function PortfolioTree() {
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<FilterChip>('all')
  const [openParentIds, setOpenParentIds] = useState<Set<number>>(new Set())

  const [reParentOpen, setReParentOpen] = useState(false)
  const [reParentChild, setReParentChild] = useState<{ asin: string; parentId: number } | null>(null)

  const [assignOpen, setAssignOpen] = useState(false)
  const [assignCampaign, setAssignCampaign] = useState<{ id: number; name: string } | null>(null)

  const [builderOpen, setBuilderOpen] = useState(false)
  const [builderParentId, setBuilderParentId] = useState<number | null>(null)

  const { data: parents, isLoading: parentsLoading, error: parentsError } = useParents()
  const { data: unmatched } = useUnmatchedCampaigns()

  const mockActive = isPortfolioMockActive()

  const filteredParents = useMemo(() => {
    const list = parents ?? []
    return list.filter((p) =>
      matchesTextFilter([p.amazonParentAsin, p.name, p.ppcCode], search),
    )
  }, [parents, search])

  function toggleParent(id: number) {
    setOpenParentIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function openReParent(asin: string, parentId: number) {
    setReParentChild({ asin, parentId })
    setReParentOpen(true)
  }

  function openAssign(id: number, name: string) {
    setAssignCampaign({ id, name })
    setAssignOpen(true)
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold">Portfolio-Management</h1>
        <p className="text-sm text-[var(--muted-foreground)]">
          Parent → Children → Auto-Kampagnen · Legacy & Nicht zugeordnet
        </p>
      </div>

      {/* Mock-Info */}
      {mockActive && (
        <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <strong>Portfolio-API noch nicht deployed</strong> — Mock-Daten aktiv. Re-Parent- und Assignment-Aktionen
            verändern noch keine echten Daten. Nach Backend-Deploy automatischer Switch auf Live-Endpoints.
          </div>
        </div>
      )}

      {/* Filter-Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted-foreground)]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="PPC-Code, ASIN oder Name..."
            className="h-9 w-full rounded-md border border-[var(--border)] bg-[var(--background)] pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-[var(--ring)]"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {FILTER_CHIPS.map((chip) => (
            <button
              key={chip.id}
              onClick={() => setFilter(chip.id)}
              className={cn(
                'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                filter === chip.id
                  ? 'border-zinc-900 bg-zinc-900 text-white'
                  : 'border-[var(--border)] bg-[var(--background)] text-[var(--muted-foreground)] hover:bg-[var(--muted)]',
              )}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      {/* Parents-Liste */}
      {parentsLoading ? (
        <div className="py-12 text-center text-sm text-[var(--muted-foreground)]">Laden...</div>
      ) : parentsError ? (
        <div className="py-12 text-center text-sm text-red-600">
          Fehler: {(parentsError as Error).message}
        </div>
      ) : filteredParents.length === 0 ? (
        <div className="py-12 text-center text-sm text-[var(--muted-foreground)]">
          Keine Parents gefunden
        </div>
      ) : (
        <div className="space-y-2">
          {filteredParents.map((p) => (
            <ParentNode
              key={p.id}
              parent={p}
              open={openParentIds.has(p.id)}
              onToggle={() => toggleParent(p.id)}
              filter={filter}
              onReParent={openReParent}
              onAssignCampaign={openAssign}
              onOpenBuilder={(pid) => {
                setBuilderParentId(pid)
                setBuilderOpen(true)
              }}
            />
          ))}
        </div>
      )}

      {/* Nicht zugeordnete Kampagnen */}
      <UnmatchedBucket unmatched={unmatched} onAssignCampaign={openAssign} />

      {/* Dialogs */}
      <ReParentDialog
        open={reParentOpen}
        onOpenChange={setReParentOpen}
        childAsin={reParentChild?.asin ?? null}
        currentParentId={reParentChild?.parentId ?? null}
      />
      <CampaignAssignmentDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        campaignId={assignCampaign?.id ?? null}
        campaignName={assignCampaign?.name}
      />
      <PortfolioBuilderWizard
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        parentId={builderParentId}
      />
    </div>
  )
}

// -------------------------------------------------------------------
// Parent-Knoten (lädt Tree lazy beim ersten Öffnen)
// -------------------------------------------------------------------

function ParentNode({
  parent,
  open,
  onToggle,
  filter,
  onReParent,
  onAssignCampaign,
  onOpenBuilder,
}: {
  parent: PortfolioParent
  open: boolean
  onToggle: () => void
  filter: FilterChip
  onReParent: (asin: string, parentId: number) => void
  onAssignCampaign: (id: number, name: string) => void
  onOpenBuilder: (parentId: number) => void
}) {
  const { data: tree, isLoading, error } = useParentTree(open ? parent.id : null)

  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--card)]">
      {/* Parent-Header */}
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-[var(--muted)]"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-[var(--muted-foreground)] shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-[var(--muted-foreground)] shrink-0" />
        )}
        <Package className="h-4 w-4 text-zinc-600 shrink-0" />
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="font-mono text-xs">{parent.amazonParentAsin}</span>
          {parent.ppcCode && (
            <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-zinc-700">
              {parent.ppcCode}
            </span>
          )}
          <span className="truncate text-sm font-medium">{parent.name}</span>
        </div>
        <div className="flex shrink-0 items-center gap-4 text-xs text-[var(--muted-foreground)]">
          <KpiInline label="Children" value={String(parent.childrenCount)} />
          <KpiInline label="Campaigns" value={String(parent.campaignsCount)} />
          <KpiInline label="Spend 30d" value={fmtEur(parent.spend30d)} />
          <KpiInline label="ACoS 30d" value={fmtPct(parent.acos30d)} />
          <KpiInline label="Orders 30d" value={fmtOrders(parent.orders30d)} />
        </div>
      </button>

      {/* Body */}
      {open && (
        <div className="border-t border-[var(--border)] px-4 py-3">
          {isLoading && (
            <div className="py-6 text-center text-xs text-[var(--muted-foreground)]">Tree laden...</div>
          )}
          {error && (
            <div className="py-6 text-center text-xs text-red-600">
              Fehler: {(error as Error).message}
            </div>
          )}
          {tree && (
            <div className="space-y-4">
              {/* Portfolio-Kampagnen */}
              <TreeSection
                icon={<Folder className="h-4 w-4 text-indigo-600" />}
                title="Portfolio-Kampagnen"
                action={
                  <button
                    onClick={() => onOpenBuilder(parent.id)}
                    title="Portfolio-Builder-Wizard oeffnen"
                    className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-xs hover:bg-[var(--muted)]"
                  >
                    <Plus className="h-3 w-3" /> Neue Portfolio-Kampagne
                  </button>
                }
              >
                {tree.portfolioCampaigns.length === 0 ? (
                  <EmptyLine text="Keine Portfolio-Kampagnen" />
                ) : (
                  <CampaignTable
                    campaigns={tree.portfolioCampaigns.filter((c) => passFilter(c, filter))}
                  />
                )}
              </TreeSection>

              {/* Children */}
              <TreeSection
                icon={<Baby className="h-4 w-4 text-teal-600" />}
                title={`Children (${tree.children.length})`}
              >
                {tree.children.length === 0 ? (
                  <EmptyLine text="Keine Children" />
                ) : (
                  <div className="space-y-2">
                    {tree.children.map((child) => (
                      <ChildBlock
                        key={child.amazonChildAsin}
                        parentId={parent.id}
                        childAsin={child.amazonChildAsin}
                        campaigns={child.autoCampaigns.filter((c) => passFilter(c, filter))}
                        onReParent={onReParent}
                      />
                    ))}
                  </div>
                )}
              </TreeSection>

              {/* Legacy */}
              {tree.legacyCampaigns.length > 0 && (
                <TreeSection
                  icon={<Archive className="h-4 w-4 text-amber-600" />}
                  title="Legacy-Kampagnen"
                >
                  <CampaignTable
                    campaigns={tree.legacyCampaigns.filter((c) => passFilter(c, filter))}
                    rowAction={() => (
                      <button
                        disabled
                        title="Aktiv in Phase (f) — In Struktur überführen"
                        className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-0.5 text-[11px] text-[var(--muted-foreground)] opacity-50 cursor-not-allowed"
                      >
                        <ArrowUpRight className="h-3 w-3" /> In Struktur überführen
                      </button>
                    )}
                    secondaryAction={(c) => (
                      <button
                        onClick={() => onAssignCampaign(c.id, c.name)}
                        className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-0.5 text-[11px] hover:bg-[var(--muted)]"
                      >
                        <Link2 className="h-3 w-3" /> Zuordnen
                      </button>
                    )}
                  />
                </TreeSection>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// -------------------------------------------------------------------
// Child-Block mit Auto-Kampagnen
// -------------------------------------------------------------------

function ChildBlock({
  parentId,
  childAsin,
  campaigns,
  onReParent,
}: {
  parentId: number
  childAsin: string
  campaigns: PortfolioCampaign[]
  onReParent: (asin: string, parentId: number) => void
}) {
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--background)]">
      <div className="flex items-center justify-between border-b border-[var(--border)] px-3 py-2">
        <div className="flex items-center gap-2">
          <FolderOpen className="h-3.5 w-3.5 text-[var(--muted-foreground)]" />
          <span className="font-mono text-xs">{childAsin}</span>
          <span className="text-[11px] text-[var(--muted-foreground)]">
            {campaigns.length} Auto-Kampagne{campaigns.length === 1 ? '' : 'n'}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            disabled
            title="Aktiv in Phase (f) — Harvest-Vorschau"
            className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-0.5 text-[11px] text-[var(--muted-foreground)] opacity-50 cursor-not-allowed"
          >
            <Sprout className="h-3 w-3" /> Harvest-Vorschau
          </button>
          <button
            onClick={() => onReParent(childAsin, parentId)}
            className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-0.5 text-[11px] hover:bg-[var(--muted)]"
          >
            <Link2 className="h-3 w-3" /> Re-Parent
          </button>
        </div>
      </div>
      <div className="px-3 py-2">
        {campaigns.length === 0 ? (
          <EmptyLine text="Keine Auto-Kampagnen in dieser Auswahl" />
        ) : (
          <CampaignTable campaigns={campaigns} />
        )}
      </div>
    </div>
  )
}

// -------------------------------------------------------------------
// Campaign-Table (geteilt für Portfolio/Auto/Legacy)
// -------------------------------------------------------------------

function CampaignTable({
  campaigns,
  rowAction,
  secondaryAction,
}: {
  campaigns: PortfolioCampaign[]
  rowAction?: (c: PortfolioCampaign) => React.ReactNode
  secondaryAction?: (c: PortfolioCampaign) => React.ReactNode
}) {
  if (campaigns.length === 0) {
    return <EmptyLine text="Keine Treffer in dieser Auswahl" />
  }
  return (
    <div className="overflow-x-auto rounded-md border border-[var(--border)]">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-[var(--border)] bg-[var(--muted)] text-[var(--muted-foreground)]">
            <th className="px-2 py-1.5 text-left font-medium">Name</th>
            <th className="px-2 py-1.5 text-left font-medium">Typ</th>
            <th className="px-2 py-1.5 text-left font-medium">Rolle</th>
            <th className="px-2 py-1.5 text-left font-medium">State</th>
            <th className="px-2 py-1.5 text-right font-medium">Spend 30d</th>
            <th className="px-2 py-1.5 text-right font-medium">ACoS 30d</th>
            <th className="px-2 py-1.5 text-right font-medium">Orders 30d</th>
            <th className="px-2 py-1.5 text-left font-medium">Status</th>
            {(rowAction || secondaryAction) && (
              <th className="px-2 py-1.5 text-right font-medium">Aktion</th>
            )}
          </tr>
        </thead>
        <tbody>
          {campaigns.map((c) => (
            <tr key={c.id} className="border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--muted)]">
              <td className="max-w-[320px] truncate px-2 py-1.5 font-medium">{c.name}</td>
              <td className="px-2 py-1.5">
                <AdTypeBadge adType={c.adType} />
              </td>
              <td className="px-2 py-1.5 capitalize">{c.role.replace('_', ' ')}</td>
              <td className="px-2 py-1.5">
                <StateBadge state={c.state} />
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmtEur(c.spend30d)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmtPct(c.acos30d)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{fmtOrders(c.orders30d)}</td>
              <td className="px-2 py-1.5">
                <CampaignPerformanceBadge campaign={c} />
              </td>
              {(rowAction || secondaryAction) && (
                <td className="px-2 py-1.5">
                  <div className="flex items-center justify-end gap-1.5">
                    {secondaryAction?.(c)}
                    {rowAction?.(c)}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function TreeSection({
  icon,
  title,
  action,
  children,
}: {
  icon: React.ReactNode
  title: string
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="text-sm font-semibold">{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </div>
  )
}

function KpiInline({ label, value }: { label: string; value: string }) {
  return (
    <div className="hidden flex-col text-right lg:flex">
      <span className="text-[10px] uppercase tracking-wider">{label}</span>
      <span className="tabular-nums text-[var(--foreground)]">{value}</span>
    </div>
  )
}

function EmptyLine({ text }: { text: string }) {
  return <div className="py-2 text-center text-xs text-[var(--muted-foreground)]">{text}</div>
}

// -------------------------------------------------------------------
// Unmatched-Bucket
// -------------------------------------------------------------------

function UnmatchedBucket({
  unmatched,
  onAssignCampaign,
}: {
  unmatched: PortfolioUnmatchedCampaign[] | undefined
  onAssignCampaign: (id: number, name: string) => void
}) {
  const [open, setOpen] = useState(true)
  const list = unmatched ?? []
  return (
    <div className="rounded-md border border-dashed border-amber-300 bg-amber-50/30">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-amber-50"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-amber-700" />
        ) : (
          <ChevronRight className="h-4 w-4 text-amber-700" />
        )}
        <HelpCircle className="h-4 w-4 text-amber-700" />
        <span className="text-sm font-semibold text-amber-900">
          Nicht zugeordnete Kampagnen
        </span>
        <span className="ml-auto flex items-center gap-1 text-xs text-amber-800">
          <AlertTriangle className="h-3.5 w-3.5" />
          {list.length}
        </span>
      </button>
      {open && (
        <div className="border-t border-amber-200 px-4 py-3">
          {list.length === 0 ? (
            <EmptyLine text="Alle Kampagnen sind einem Parent zugeordnet." />
          ) : (
            <div className="overflow-x-auto rounded-md border border-[var(--border)] bg-[var(--card)]">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[var(--border)] bg-[var(--muted)] text-[var(--muted-foreground)]">
                    <th className="px-2 py-1.5 text-left font-medium">Name</th>
                    <th className="px-2 py-1.5 text-left font-medium">Typ</th>
                    <th className="px-2 py-1.5 text-left font-medium">State</th>
                    <th className="px-2 py-1.5 text-right font-medium">Spend 30d</th>
                    <th className="px-2 py-1.5 text-right font-medium">ACoS 30d</th>
                    <th className="px-2 py-1.5 text-right font-medium">Orders 30d</th>
                    <th className="px-2 py-1.5 text-right font-medium">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((c) => (
                    <tr key={c.id} className="border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--muted)]">
                      <td className="max-w-[320px] truncate px-2 py-1.5 font-medium">{c.name}</td>
                      <td className="px-2 py-1.5">
                        <AdTypeBadge adType={c.adType} />
                      </td>
                      <td className="px-2 py-1.5">
                        <StateBadge state={c.state} />
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmtEur(c.spend30d)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmtPct(c.acos30d)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{fmtOrders(c.orders30d)}</td>
                      <td className="px-2 py-1.5 text-right">
                        <button
                          onClick={() => onAssignCampaign(c.id, c.name)}
                          className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-0.5 text-[11px] hover:bg-[var(--muted)]"
                        >
                          <Link2 className="h-3 w-3" /> Zuordnen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// -------------------------------------------------------------------
// Filter-Logik
// -------------------------------------------------------------------

function passFilter(c: PortfolioCampaign, filter: FilterChip): boolean {
  if (filter === 'all') return true
  const status = deriveStatus(c)
  if (filter === 'harvested') return status === 'harvested'
  if (filter === 'review') return status === 'review'
  if (filter === 'performing') return status === 'performing'
  if (filter === 'legacy') return c.role === 'migration_source'
  return true
}
